import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Gift, Check, Loader2 } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { BRAND_COLORS, withAlpha } from "../../lib/bookingStatus";

/**
 * Feb 2026 · Master-Editor für die Zusatzleistungen (Extras).
 * Diese Liste ist der Katalog, aus dem Kunden im Buchungs-Wizard einzelne
 * Extras buchen können (Sauna vorheizen, Blumen-Willkommen, …).
 *
 * User-Directive: „Einfache Liste mit Bearbeiten- und Löschen-Button pro
 * Eintrag." — analog zu StandardServicesSection, ohne Live-Preview.
 */

const CATEGORY_LABEL = {
  interior:   "Innenbereich",
  outdoor:    "Außenbereich",
  additional: "Zusätzlich",
};
const CATEGORIES = ["interior", "outdoor", "additional"];

export function ExtrasSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/extras");
      const list = Array.isArray(data) ? [...data] : [];
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(list);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doDelete = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Zusatzleistung „${item.name_de}" endgültig aus dem Katalog entfernen?`)) return;
    setBusy(true);
    try {
      await api.delete(`/team/extras/${item.key}`);
      toast.success("Zusatzleistung entfernt.");
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const grouped = CATEGORIES.map((cat) => ({
    cat,
    list: items.filter((it) => (it.category || "additional") === cat),
  }));

  return (
    <section
      className="bg-white border border-navy/10 p-6 space-y-4"
      data-testid="master-extras-section"
    >
      <header className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: withAlpha(BRAND_COLORS.gold, 0.14), color: BRAND_COLORS.goldDark }}
        >
          <Gift className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg text-navy leading-tight">
            Zusatzleistungen · Buchbare Extras
          </div>
          <div className="text-[12px] text-navy-dark/60">
            {items.length} Extra{items.length === 1 ? "" : "s"} · Kunden buchen sie einzeln im Ab-/Anreise-Wizard
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={busy || adding}
          data-testid="master-ext-add-btn"
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-50 px-3 py-2 text-[11.5px] tracking-[0.14em] uppercase"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          Hinzufügen
        </button>
      </header>

      {loading ? (
        <div className="text-[13px] text-navy-dark/60 italic p-3 inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird geladen …
        </div>
      ) : (
        <div className="space-y-5">
          {adding && (
            <ExtraAddRow
              onCancel={() => setAdding(false)}
              onSaved={() => { setAdding(false); load(); }}
            />
          )}
          {grouped.map(({ cat, list }) =>
            list.length === 0 ? null : (
              <div key={cat} data-testid={`master-ext-group-${cat}`}>
                <div className="label-caps text-navy-dark/70 mb-2">
                  {CATEGORY_LABEL[cat]} · {list.length}
                </div>
                <ul className="space-y-1.5 list-none pl-0 m-0">
                  {list.map((it) => (
                    <ExtraRow
                      key={it.key}
                      item={it}
                      isEditing={editingKey === it.key}
                      onEdit={() => setEditingKey(it.key)}
                      onCancel={() => setEditingKey(null)}
                      onDelete={() => doDelete(it)}
                      onSaved={() => { setEditingKey(null); load(); }}
                      busy={busy}
                    />
                  ))}
                </ul>
              </div>
            ),
          )}
          {items.length === 0 && !adding && (
            <div className="text-[13px] text-navy-dark/60 italic p-3 border border-dashed border-navy/15">
              Noch keine Zusatzleistungen — bitte oben „Hinzufügen".
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ExtraRow({ item, isEditing, onEdit, onCancel, onDelete, onSaved, busy }) {
  const [nameDe, setNameDe] = useState(item.name_de || "");
  const [nameEn, setNameEn] = useState(item.name_en || "");
  const [cat, setCat] = useState(item.category || "additional");
  const [order, setOrder] = useState(item.order ?? 0);
  const [descDe, setDescDe] = useState(item.description_de || "");
  const [priceHint, setPriceHint] = useState(item.price_hint || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNameDe(item.name_de || "");
    setNameEn(item.name_en || "");
    setCat(item.category || "additional");
    setOrder(item.order ?? 0);
    setDescDe(item.description_de || "");
    setPriceHint(item.price_hint || "");
  }, [item, isEditing]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/team/extras/${item.key}`, {
        name_de: nameDe.trim(),
        name_en: nameEn.trim(),
        category: cat,
        order: Number(order) || 0,
        description_de: descDe.trim() || null,
        price_hint: priceHint.trim() || null,
      });
      toast.success("Gespeichert.");
      onSaved?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!isEditing) {
    return (
      <li
        className="flex items-start gap-2 border border-navy/10 bg-white px-3 py-2"
        data-testid={`master-ext-row-${item.key}`}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] text-navy leading-snug">
            {item.name_de}
            {item.price_hint && (
              <span
                className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10.5px] tracking-wider uppercase"
                style={{
                  background: withAlpha(BRAND_COLORS.gold, 0.14),
                  color: BRAND_COLORS.goldDark,
                }}
              >
                {item.price_hint}
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-navy-dark/50 mt-0.5">
            {item.name_en} · Key <code className="text-navy-dark/70">{item.key}</code> · Reihenfolge {item.order ?? 0}
          </div>
          {item.description_de && (
            <div className="text-[12px] text-navy-dark/65 italic mt-1 leading-relaxed">
              {item.description_de}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onEdit}
          disabled={busy}
          data-testid={`master-ext-edit-${item.key}`}
          aria-label="Bearbeiten"
          className="text-navy-dark/50 hover:text-navy p-1.5 border border-navy/15 hover:border-navy/40"
        >
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.7} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          data-testid={`master-ext-delete-${item.key}`}
          aria-label="Löschen"
          className="text-navy-dark/50 hover:text-red-attn p-1.5 border border-navy/15 hover:border-red-attn"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} />
        </button>
      </li>
    );
  }
  return (
    <li className="border border-gold bg-ivory/50 p-3" data-testid={`master-ext-row-${item.key}-editing`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Name (DE) *
          <input
            type="text" value={nameDe} onChange={(e) => setNameDe(e.target.value)} maxLength={180}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid={`master-ext-input-de-${item.key}`}
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Name (EN)
          <input
            type="text" value={nameEn} onChange={(e) => setNameEn(e.target.value)} maxLength={180}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid={`master-ext-input-en-${item.key}`}
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Kategorie
          <select
            value={cat} onChange={(e) => setCat(e.target.value)}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid={`master-ext-input-cat-${item.key}`}
          >
            <option value="interior">Innenbereich</option>
            <option value="outdoor">Außenbereich</option>
            <option value="additional">Zusätzlich</option>
          </select>
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Reihenfolge
          <input
            type="number" value={order} onChange={(e) => setOrder(e.target.value)}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal"
            data-testid={`master-ext-input-order-${item.key}`}
          />
        </label>
        <label className="md:col-span-2 text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Beschreibung (DE, optional)
          <textarea
            value={descDe} onChange={(e) => setDescDe(e.target.value)} rows={2} maxLength={400}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case resize-y"
            data-testid={`master-ext-input-desc-${item.key}`}
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Preis-Hinweis (optional)
          <input
            type="text" value={priceHint} onChange={(e) => setPriceHint(e.target.value)}
            placeholder="z. B. 80 € · auf Anfrage"
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid={`master-ext-input-price-${item.key}`}
          />
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button" onClick={onCancel} disabled={saving}
          data-testid={`master-ext-cancel-${item.key}`}
          className="text-[11px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy px-3 py-1.5 border border-navy/15 disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button" onClick={save} disabled={saving || !nameDe.trim()}
          data-testid={`master-ext-save-${item.key}`}
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase"
        >
          <Check className="w-3.5 h-3.5" strokeWidth={2} />
          {saving ? "…" : "Speichern"}
        </button>
      </div>
    </li>
  );
}

function ExtraAddRow({ onCancel, onSaved }) {
  const [key, setKey] = useState("");
  const [nameDe, setNameDe] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [cat, setCat] = useState("additional");
  const [descDe, setDescDe] = useState("");
  const [priceHint, setPriceHint] = useState("");
  const [saving, setSaving] = useState(false);

  const derivedKey = (key || nameDe)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 32);

  const save = async () => {
    if (!nameDe.trim()) { toast.error("Name (DE) fehlt."); return; }
    if (!derivedKey) { toast.error("Key konnte nicht abgeleitet werden — bitte manuell setzen."); return; }
    setSaving(true);
    try {
      await api.post("/team/extras", {
        key: derivedKey,
        name_de: nameDe.trim(),
        name_en: (nameEn || nameDe).trim(),
        category: cat,
        order: 999,
        description_de: descDe.trim() || null,
        price_hint: priceHint.trim() || null,
      });
      toast.success("Zusatzleistung angelegt.");
      onSaved?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="border-2 border-dashed border-gold p-4 bg-ivory/40" data-testid="master-ext-add-row">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Name (DE) *
          <input
            type="text" value={nameDe} onChange={(e) => setNameDe(e.target.value)}
            placeholder="z. B. Sauna vorheizen"
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid="master-ext-add-de"
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Name (EN)
          <input
            type="text" value={nameEn} onChange={(e) => setNameEn(e.target.value)}
            placeholder="Fällt leer → wird aus DE übernommen"
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid="master-ext-add-en"
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Key (optional · leer = automatisch)
          <input
            type="text" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder={derivedKey || "z. B. sauna-preheat"}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case font-mono"
            data-testid="master-ext-add-key"
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Kategorie
          <select
            value={cat} onChange={(e) => setCat(e.target.value)}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid="master-ext-add-cat"
          >
            <option value="interior">Innenbereich</option>
            <option value="outdoor">Außenbereich</option>
            <option value="additional">Zusätzlich</option>
          </select>
        </label>
        <label className="md:col-span-2 text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Beschreibung (DE, optional)
          <textarea
            value={descDe} onChange={(e) => setDescDe(e.target.value)} rows={2} maxLength={400}
            placeholder="Kurze Beschreibung, die Kunden im Wizard sehen."
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case resize-y"
            data-testid="master-ext-add-desc"
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Preis-Hinweis (optional)
          <input
            type="text" value={priceHint} onChange={(e) => setPriceHint(e.target.value)}
            placeholder="z. B. 80 €"
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid="master-ext-add-price"
          />
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button" onClick={onCancel} disabled={saving}
          data-testid="master-ext-add-cancel"
          className="text-[11px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy px-3 py-1.5 border border-navy/15 disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button" onClick={save} disabled={saving || !nameDe.trim()}
          data-testid="master-ext-add-save"
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          {saving ? "…" : "Anlegen"}
        </button>
      </div>
    </div>
  );
}
